import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as crypto from 'crypto-js';
import { Observable, Subject, combineLatest, filter, map } from 'rxjs';

import { communitiesIcons } from '../../../../assets/dist/communities-icons';
import { getEnvironment } from '../../../shared/environment';
import { CommunitiesEnvironments } from '../../../shared/environment/models/communities-environments.enum';
import { RegistrationRequest } from '../../../shared/user/registration-request.model';
import { RegistrationSearchRequest } from '../../../shared/user/registration-search-request.model';
import {
  RegistrationFormInitialValue,
  RegistrationFormValue,
} from '../../models/registration-form.model';
import { RegistrationStore } from '../../state/registration.store';

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RegistrationStore],
  template: `
    <img class="h-40" alt="" [src]="logoUrl" />

    <div class="border-1 border-petro-n2 mt-48 w-[504px] rounded-2xl bg-white px-40 pb-40 pt-48">
      <div
        *ngIf="(userFetchStatus$ | async) === 'failure'; else form"
        data-testid="main-user-register-v2--div--error"
        class="flex h-[584px] flex-col items-center justify-center"
      >
        <tsq-icon [icon]="triangleIcon" [classes]="'size-80 text-coal-tertiary mb-24'" />

        <h2>{{ 'COMMON_ERROR_TITLE' | translate }}</h2>
        <p class="text-coal-secondary mb-0 mt-8">{{ 'COMMON_ERROR_TEXT' | translate }}</p>

        <tsq-button
          class="mt-24"
          schema="new-primary"
          kind="secondary"
          [text]="'TRY_AGAIN' | translate"
          (clicked)="fetchUser()"
        />
      </div>

      <ng-template #form>
        <ng-container [ngSwitch]="registrationStatus$ | async">
          <ng-container *ngSwitchCase="'success'">
            <h1 data-testid="main-user-register-v2--h1--success">
              {{ 'REGISTER.SIGN_UP_SUCCESSFUL' | translate }}
            </h1>
            <p class="font-semi-bold text-md mb-0 mt-8">
              {{ 'REGISTER.YOU_CAN_NOW_LOG_IN' | translate }}
            </p>

            <tsq-button
              class="mt-40"
              schema="new-primary"
              size="lg"
              route="login"
              [text]="'REGISTER.BACK_TO_LOGIN' | translate"
            />
          </ng-container>

          <comm-user-register-sign-up-form
            *ngSwitchDefault
            [isRegistering]="(registrationStatus$ | async) === 'loading'"
            [isFetchingUser]="(userFetchStatus$ | async) === 'loading'"
            [initialData]="initialUserData$ | async"
            (signUp)="signUpSubject.next($event)"
          />
        </ng-container>
      </ng-template>
    </div>
  `,
  styles: [
    `
      :host {
        @apply bg-petro-n1 font-base block flex min-h-screen flex-col items-center py-80;
      }
    `,
  ],
})
export class MainUserRegisterV2Component implements OnInit {
  private readonly registrationStore = inject(RegistrationStore);
  private readonly activatedRoute = inject(ActivatedRoute);

  protected readonly initialUserData$: Observable<RegistrationFormInitialValue> =
    this.registrationStore.userRegister$.pipe(
      filter(val => !!val?.first_name && !!val?.last_name),
      map(userRegister => ({
        name: userRegister.first_name,
        lastName: userRegister.last_name,
      })),
    );

  protected readonly registrationStatus$ = this.registrationStore.registrationStatus$;
  protected readonly userFetchStatus$ = this.registrationStore.userFetchStatus$;

  protected readonly signUpSubject = new Subject<RegistrationFormValue>();
  protected readonly fetchUserSubject = new Subject<void>();

  protected readonly logoUrl =
    getEnvironment().env === CommunitiesEnvironments.BR_PROD
      ? '/assets/brand/logo.svg'
      : '/assets/brand/logo-community.svg';
  protected readonly triangleIcon = communitiesIcons.triangleExclamation;

  ngOnInit(): void {
    this.listenToFetchUserRequest();
    this.listenToSignUp();

    this.fetchUser();
  }

  fetchUser(): void {
    this.fetchUserSubject.next();
  }

  private listenToFetchUserRequest(): void {
    combineLatest([this.fetchUserSubject.asObservable(), this.activatedRoute.params])
      .pipe(
        untilDestroyed(this),
        filter(([, params]) => !!params.userId && !!params.token),
      )
      .subscribe(([, params]) => {
        const request = new RegistrationSearchRequest();
        request.registrationToken = params.token;
        request.userId = params.userId;

        this.registrationStore.fetchUserRegister(request);
      });
  }

  private listenToSignUp(): void {
    combineLatest([
      this.signUpSubject.asObservable(),
      this.activatedRoute.params,
      this.registrationStore.userRegister$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(([, params]) => !!params?.userId && !!params.token),
      )
      .subscribe(([formValue, params, userRegister]) => {
        const request = new RegistrationRequest();
        request.id = params.userId;
        request.registrationToken = params.token;
        request.email = userRegister.email;
        request.billingDocument = userRegister.billingDocument;
        request.firstName = formValue.name;
        request.lastName = formValue.lastName;
        request.hashPassword = crypto.SHA1(formValue.password).toString();

        this.registrationStore.signUp(request);
      });
  }
}
